import { Injectable, Scopes, ControllerProtocol } from '@visiba/instantiation';
import { CreateNewMessageAnalyticActions, CreateNewMessageController } from '../view_create_new_message.controller';
import { Logger } from '@visiba/logger';
import { BasketService } from '../basket.service';
import { signal } from '@preact/signals-react';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { NotificationService } from '@component/notification_manager/notification/notification.service';
import { LocalPushNotification } from '@component/notification_manager/notification/local_push_notification';

@Injectable({
	scope: Scopes.Scoped,
})
export class ScreenRfvController implements ControllerProtocol {
	public screenState = signal<'ready' | 'not-ready' | 'busy'>('not-ready');

	private readonly logger = new Logger('ScreenRfvController');

	constructor(
		private readonly createNewMessageController: CreateNewMessageController,
		private readonly basketService: BasketService,
		private readonly analyticService: AnalyticService,
		private readonly notificationService: NotificationService,
	) {}

	async whenMount() {
		this.analyticService.trackPageView({
			customTitle: 'Create new message: Reason for visit',
		});

		if (!this.allowedToBeHere()) {
			this.analyticService.trackEvent(
				AnalyticCategories.CreateNewMessage,
				CreateNewMessageAnalyticActions.NotAllowedToBeHere,
				'Reason for visit',
			);
			this.createNewMessageController.goToScreen('start');

			return;
		}

		if (this.createNewMessageController.getFilteredReasonForVisits().length === 0) {
			const reasonForVisit = this.createNewMessageController.getFlowSettings().predefinedReasonForVisit?.[0] ?? null;

			if (reasonForVisit) {
				this.analyticService.trackEvent(
					AnalyticCategories.CreateNewMessage,
					CreateNewMessageAnalyticActions.AutoSetReasonForVisit,
					`${reasonForVisit}`,
				);
			} else {
				this.analyticService.trackEvent(AnalyticCategories.CreateNewMessage, CreateNewMessageAnalyticActions.NoReasonForVisits);
			}

			this.screenState.value = 'busy';

			await this.setReasonForVisit(reasonForVisit);
			this.createNewMessageController.goToScreen('forms', { replace: true });

			return;
		}

		this.screenState.value = 'ready';
	}

	public async selectReasonForVisit(reasonForVisitId: number) {
		this.analyticService.trackEvent(
			AnalyticCategories.CreateNewMessage,
			CreateNewMessageAnalyticActions.SelectReasonForVisit,
			`${reasonForVisitId}`,
		);

		this.screenState.value = 'busy';
		const response = await this.setReasonForVisit(reasonForVisitId);
		if (response.err) {
			this.notificationService.add(
				new LocalPushNotification({
					label: 'Error',
					message: response.err.message,
					variant: 'danger',
					type: 'toast',
				}),
			);

			this.screenState.value = 'ready';

			return;
		}

		this.createNewMessageController.goToScreen('forms');
	}

	private async setReasonForVisit(reasonForVisitId: number | null): AsyncResult<void> {
		this.logger.logInformation('Select reason for visit');

		if (this.basketService.reasonForVisitId !== reasonForVisitId) {
			const flowSettings = this.createNewMessageController.getFlowSettings();
			const postResponse = await this.basketService.addData({
				reasonForVisitId,
				redRobinSessionId: flowSettings.redRobinSessionId,
				asAgentFor: flowSettings.asAgentFor,
			});
			if (postResponse.err) {
				this.logger.logError('User tried to post reason for visit');

				return postResponse;
			}
		}

		return Ok();
	}

	private allowedToBeHere() {
		// If we don't have flow data, we can't be here.
		const flowData = this.createNewMessageController.flowData.value.value;
		if (!flowData) {
			return false;
		}

		return true;
	}
}
